<template>
    <VScroll class="request-related">
        <div class="request-related__control">
            <VButton
                class="button_green-light request-related__toggle"
                type="button"
                v-if="!isPickerOpen"
                @click="openPicker"
            >
                Привязать обращение
            </VButton>

            <div class="request-related__picker" v-else>
                <div class="request-related__search">
                    <VInput
                        class="request-related__search-input"
                        placeholder="Номер обращения"
                        inputmode="numeric"
                        :value="search"
                        @input="onSearch"
                    ></VInput>

                    <button class="request-related__close" type="button" @click="closePicker">Отмена</button>
                </div>

                <p class="request-related__hint" v-if="isSearching">Поиск…</p>
                <p class="request-related__hint" v-else-if="searchError">{{ searchError }}</p>
                <p class="request-related__hint" v-else-if="search && !candidateRows.length">
                    Нет обращений, которые можно привязать
                </p>

                <ul class="request-related__list request-related__list_candidates" v-else-if="candidateRows.length">
                    <li class="request-related__row" :key="row.id" v-for="row in candidateRows">
                        <div class="request-related__main">
                            <span class="request-related__number">{{ row.number }}</span>
                            <RequestPriorityBadge :priority="row.priority"></RequestPriorityBadge>
                            <span class="request-related__status" :style="{ color: row.statusColor }">
                                {{ row.statusTitle }}
                            </span>
                        </div>

                        <div class="request-related__meta">
                            {{ row.assignee }} · <span>{{ row.deadlineText }}</span>
                        </div>

                        <VButton
                            class="button_green request-related__action"
                            type="button"
                            :progress="pendingId === row.id"
                            :disabled="pendingId !== null"
                            @click="attach(row)"
                        >
                            Привязать
                        </VButton>
                    </li>
                </ul>
            </div>
        </div>

        <div class="request-related__loader" v-if="isLoading">
            <VSpinner></VSpinner>
        </div>

        <template v-else>
            <p class="request-related__error" v-if="error">{{ error }}</p>

            <p class="request-related__empty" v-else-if="!rows.length">Связанных обращений нет</p>

            <ul class="request-related__list" v-else>
                <li
                    class="request-related__row"
                    :class="{ 'request-related__row_unread': row.hasUnread }"
                    :key="row.id"
                    v-for="row in rows"
                >
                    <div class="request-related__main">
                        <span
                            class="request-related__unread"
                            v-if="row.hasUnread"
                            title="Есть непрочитанные сообщения"
                            aria-label="Есть непрочитанные сообщения"
                        ></span>
                        <router-link class="request-related__number" :to="requestLink(row)">{{
                            row.number
                        }}</router-link>
                        <RequestPriorityBadge :priority="row.priority"></RequestPriorityBadge>
                        <span class="request-related__status" :style="{ color: row.statusColor }">
                            {{ row.statusTitle }}
                        </span>
                    </div>

                    <div class="request-related__meta">
                        {{ row.assignee }} ·
                        <span :class="{ 'request-related__deadline_overdue': row.overdue }">{{
                            row.deadlineText
                        }}</span>
                    </div>

                    <button
                        class="request-related__remove"
                        type="button"
                        title="Отвязать"
                        aria-label="Отвязать"
                        :disabled="pendingId !== null"
                        @click="confirmDetach(row)"
                    >
                        ×
                    </button>
                </li>
            </ul>
        </template>
    </VScroll>
</template>
<script>
// Components
import VButton from '@/views/components/VButton/VButton.vue';
import VInput from '@/views/components/VInput/VInput.vue';
import VScroll from '@/views/components/VScroll/VScroll.vue';
import VSpinner from '@/views/components/VProgress/VSpinner.vue';
import RequestPriorityBadge from '@/views/components/RequestPriority/RequestPriorityBadge.vue';

// Other
import Vue from 'vue';
import Component from 'vue-class-component';
import { Prop, Watch } from 'vue-property-decorator';
import debounce from 'lodash/debounce';
import ModalNames from '@/js/enums/ModalNames';
import {
    getRelatedRequestCandidates,
    getRelatedRequests,
    linkRequestToParent,
    unlinkRequestFromParent,
} from '@/js/api';
import { errorMessage } from '@/js/error';
import { filterCandidates, relatedRow } from '@/js/requestRelations';

/**
 * Вкладка «Связанные обращения» главного обращения (массовая проблема).
 *
 * Показывается только исполнителю и администратору — это решает RequestView. Список
 * бэкенд уже сузил до обращений, которые сотрудник может открыть; кандидатов на
 * привязку — тоже (а заодно отсёк те, что дали бы цикл или второй уровень).
 *
 * Привязка — только группировка: статусы, приоритеты, сроки, исполнители и комментарии
 * связанных обращений здесь не меняются и не синхронизируются.
 */
@Component({
    components: {
        VButton,
        VInput,
        VScroll,
        VSpinner,
        RequestPriorityBadge,
    },
})
class RequestViewTabRelated extends Vue {
    @Prop({ type: Object, required: true })
    request;

    children = [];
    isLoading = false;
    error = '';

    isPickerOpen = false;
    search = '';
    candidates = [];
    isSearching = false;
    searchError = '';

    pendingId = null;

    // Номер последнего fetch(): ответ про предыдущее обращение не должен лечь на текущее.
    fetchToken = 0;

    get rows() {
        return this.children.map((child) => relatedRow(child));
    }

    get candidateRows() {
        return filterCandidates(this.request.id, this.children, this.candidates).map((candidate) =>
            relatedRow(candidate),
        );
    }

    requestLink(row) {
        return { name: 'request', params: { id: row.id, method: this.$route.params.method } };
    }

    async fetch() {
        const token = ++this.fetchToken;

        this.isLoading = true;
        this.error = '';

        try {
            const response = await getRelatedRequests(this.request.id);

            if (token !== this.fetchToken) {
                return;
            }

            this.children = Array.isArray(response.data.children) ? response.data.children : [];
            this.$emit('loaded', this.children.length);
        } catch (requestError) {
            if (token !== this.fetchToken) {
                return;
            }

            this.error = errorMessage(requestError);
            this.children = [];
        } finally {
            if (token === this.fetchToken) {
                this.isLoading = false;
            }
        }
    }

    openPicker() {
        this.isPickerOpen = true;
    }

    closePicker() {
        this.isPickerOpen = false;
        this.search = '';
        this.candidates = [];
        this.searchError = '';
        this.debouncedSearch.cancel();
    }

    onSearch(value) {
        this.search = value;

        if (!String(value).trim()) {
            this.debouncedSearch.cancel();
            this.candidates = [];
            this.isSearching = false;

            return;
        }

        this.isSearching = true;
        this.debouncedSearch(value);
    }

    async runSearch(value) {
        this.searchError = '';

        try {
            const response = await getRelatedRequestCandidates(this.request.id, String(value).trim());

            if (value !== this.search) {
                return;
            }

            this.candidates = Array.isArray(response.data) ? response.data : [];
        } catch (requestError) {
            this.searchError = errorMessage(requestError);
            this.candidates = [];
        } finally {
            if (value === this.search) {
                this.isSearching = false;
            }
        }
    }

    async attach(row) {
        if (this.pendingId !== null) {
            return;
        }

        this.pendingId = row.id;

        try {
            // Привязывается найденное обращение: главным становится текущее.
            await linkRequestToParent(row.id, this.request.id);
            this.closePicker();
            await this.fetch();
        } catch (requestError) {
            this.$showModal(ModalNames.ERROR, { errorMessage: errorMessage(requestError) });
        } finally {
            this.pendingId = null;
        }
    }

    confirmDetach(row) {
        this.$showModal(ModalNames.CONFIRM, {
            title: 'Отвязать обращение',
            text: `Отвязать обращение ${row.number}? Само обращение не изменится.`,
            cancelTitle: 'Отмена',
            confirmTitle: 'Отвязать',
            confirmAction: () => this.detach(row),
        });
    }

    async detach(row) {
        this.pendingId = row.id;

        try {
            await unlinkRequestFromParent(row.id);
            await this.fetch();
        } catch (requestError) {
            this.$showModal(ModalNames.ERROR, { errorMessage: errorMessage(requestError) });
        } finally {
            this.pendingId = null;
        }
    }

    @Watch('request.id')
    onRequestChanged() {
        this.closePicker();
        this.fetch();
    }

    created() {
        this.debouncedSearch = debounce(this.runSearch, 300);
    }

    mounted() {
        this.fetch();
    }

    beforeDestroy() {
        this.debouncedSearch.cancel();
    }
}

export default RequestViewTabRelated;
</script>
<style lang="scss">
.request-related {
    &__control,
    &__loader,
    &__empty,
    &__error,
    & > .request-related__list {
        margin: 16px 24px;
    }

    &__loader {
        display: flex;
        justify-content: center;
    }

    &__empty,
    &__hint {
        color: #808080;
        font-size: 0.875rem;
    }

    &__hint {
        margin: 12px 0 0;
    }

    &__error {
        color: var(--error-color);
        font-size: 0.875rem;
    }

    &__search {
        display: flex;
        gap: 12px;
        align-items: center;
    }

    &__search-input {
        flex: 1 1 auto;
        min-width: 0;
    }

    &__close {
        flex: 0 0 auto;
        color: #5f6368;
        font-size: 0.875rem;
    }

    &__list {
        container-type: inline-size;
        padding: 0;
        list-style: none;

        &_candidates {
            margin: 12px 0 0;
        }
    }

    // Компактная строка вместо таблицы: панель RequestView бывает узкой, а горизонтальной
    // прокрутки здесь быть не должно. Основная линия — номер, приоритет, статус и действие;
    // исполнитель и срок — второй линией, а на широкой панели поднимаются в первую.
    &__row {
        display: grid;
        grid-template-columns: minmax(0, 1fr) auto;
        grid-template-areas:
            'main action'
            'meta meta';
        column-gap: 12px;
        row-gap: 2px;
        align-items: center;
        padding: 8px 12px;
        border: 1px solid #e4eaef;
        border-radius: 10px;

        &_unread {
            background: #f2f8ff;
            border-color: #c6dcf5;
        }

        & + & {
            margin-top: 6px;
        }
    }

    &__main {
        grid-area: main;
        display: flex;
        flex-wrap: wrap;
        gap: 8px;
        align-items: center;
        min-width: 0;
    }

    &__meta {
        grid-area: meta;
        min-width: 0;
        overflow: hidden;
        color: #5f6368;
        font-size: 0.8125rem;
        white-space: nowrap;
        text-overflow: ellipsis;
    }

    &__remove,
    &__action {
        grid-area: action;
    }

    // Ширина самой панели, а не окна: RequestView живёт в splitpanes и бывает узкой
    // на любом мониторе.
    @container (min-width: 520px) {
        &__row {
            grid-template-columns: auto minmax(0, 1fr) auto;
            grid-template-areas: 'main meta action';
        }
    }

    &__unread {
        flex: 0 0 auto;
        width: 8px;
        height: 8px;
        border-radius: 50%;
        background: #1a73e8;
    }

    &__number {
        color: #202124;
        font-size: 0.875rem;
        font-weight: 600;
        white-space: nowrap;
    }

    a.request-related__number {
        color: #1a73e8;

        &:hover {
            text-decoration: underline;
        }
    }

    &__status {
        font-size: 0.8125rem;
        white-space: nowrap;
    }

    &__deadline_overdue {
        color: var(--error-color);
    }

    &__remove {
        width: 28px;
        height: 28px;
        color: #5f6368;
        font-size: 1.25rem;
        line-height: 1;
        border-radius: 6px;

        &:hover:not(:disabled) {
            color: var(--error-color);
            background: #fdecea;
        }

        &:disabled {
            opacity: 0.4;
        }
    }
}
</style>
